import React from "react";
import { motion } from "framer-motion";
import {
  Buildings,
  Target,
  Eye,
  CheckCircle,
  UserCircle,
  Briefcase,
  Calendar,
  IdentificationCard,
} from "@phosphor-icons/react";
import PageHero from "../components/site/PageHero";
import { COMPANY } from "../data/site";

const milestones = [
  {
    year: "2024",
    title: "Velariti Software Solutions incorporated",
    text: "Registered as a Private Limited (CIN U58200BR2024PTC072222) in Bihar, India.",
  },
  {
    year: "2024",
    title: "JayesPay product launched",
    text: "Retailer-first fintech app rolled out with recharge, bills, PAN, BC and RD.",
  },
  {
    year: "2025",
    title: "Pan-India retailer onboarding",
    text: "Digital KYC, package-based activation and mobile-first onboarding live.",
  },
  {
    year: "Soon",
    title: "AePS, IRCTC, Loans & more",
    text: "Banking, travel, lending and partner-bank accounts on the roadmap.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About JayesPay"
        title="Built in Bihar."
        highlight="Made for Bharat."
        description={`${COMPANY.name} is a product of ${COMPANY.legalName}. We are on a mission to give every Indian retailer a digital service counter — recharge, banking, payments, savings and beyond.`}
        testId="about-hero"
      />

      {/* Mission / Vision */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-2 gap-6">
          {[
            {
              Icon: Target,
              tag: "Our Mission",
              title: "Make fintech feel local.",
              text: "Bridge the gap between rural and urban retail by putting modern fintech inside every kirana, mobile shop and BC outlet — in their language, with their margins.",
            },
            {
              Icon: Eye,
              tag: "Our Vision",
              title: "One trusted app, every service.",
              text: "Be the single retailer platform for digital payments, assisted banking, savings, travel and lending — across India.",
            },
          ].map(({ Icon, tag, title, text }, i) => (
            <motion.div
              key={tag}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-surfaceAlt border border-line rounded-3xl p-8 lg:p-12 hover:border-brandBlue/40 transition-colors"
              data-testid={`about-${tag.toLowerCase().replace(" ", "-")}`}
            >
              <div className="h-14 w-14 rounded-2xl bg-brandBlue/15 text-infoText flex items-center justify-center mb-5">
                <Icon size={26} weight="duotone" />
              </div>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-ink500 mb-2">
                {tag}
              </div>
              <h2 className="font-heading font-extrabold text-ink900 text-2xl sm:text-3xl tracking-tight mb-3">
                {title}
              </h2>
              <p className="text-ink700 leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Company facts */}
      <section className="py-16 bg-surfaceAlt border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { Icon: Buildings, label: "Legal entity", value: COMPANY.legalName },
              { Icon: IdentificationCard, label: "CIN", value: COMPANY.cin },
              { Icon: Calendar, label: "Founded", value: COMPANY.foundedYear },
              { Icon: Briefcase, label: "Sector", value: "Fintech · Retailer SaaS" },
            ].map(({ Icon, label, value }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-surface border border-line rounded-2xl p-5"
                data-testid={`about-fact-${i}`}
              >
                <Icon size={22} weight="duotone" className="text-infoText mb-3" />
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-ink500 mb-1">
                  {label}
                </div>
                <div className="text-sm font-bold text-ink900 leading-snug">
                  {value}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-3">
            Our Journey
          </div>
          <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl tracking-tight mb-10">
            From idea to in-shop reality.
          </h2>

          <div className="relative">
            <div className="absolute left-3.5 sm:left-1/2 top-2 bottom-2 w-px bg-line" />
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <motion.div
                  key={m.title}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className={`relative sm:grid sm:grid-cols-2 sm:gap-8 ${
                    i % 2 === 0 ? "" : "sm:[direction:rtl]"
                  }`}
                  data-testid={`timeline-${i}`}
                >
                  <div className="pl-12 sm:pl-0 sm:[direction:ltr] sm:pr-8">
                    <div className="absolute left-0 top-0 sm:left-1/2 sm:-translate-x-1/2 h-7 w-7 rounded-full bg-brandBlue border-4 border-surface flex items-center justify-center">
                      <CheckCircle size={14} weight="fill" className="text-ink900" />
                    </div>
                    <div className="bg-surfaceAlt border border-line rounded-2xl p-5">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-infoText mb-2">
                        {m.year}
                      </div>
                      <div className="font-heading font-bold text-ink900 text-lg mb-1">
                        {m.title}
                      </div>
                      <div className="text-sm text-ink700">{m.text}</div>
                    </div>
                  </div>
                  <div className="hidden sm:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24 bg-ink900 text-surface">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brandOrange mb-3">
            Leadership
          </div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-10">
            Directors driving JayesPay forward.
          </h2>

          <div className="grid sm:grid-cols-2 gap-5">
            {COMPANY.directors.map((d, i) => (
              <motion.div
                key={d.din}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-surface/5 border border-surface/15 rounded-3xl p-7 hover:bg-surface/10 transition-colors"
                data-testid={`director-${i}`}
              >
                <div className="h-16 w-16 rounded-2xl brand-icon-tile-dark flex items-center justify-center mb-5">
                  <UserCircle size={32} weight="duotone" />
                </div>
                <div className="font-heading font-bold text-2xl tracking-tight mb-1">
                  {d.name}
                </div>
                <div className="text-sm text-brandOrange font-semibold mb-3">
                  {d.designation} · Signatory
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="uppercase tracking-[0.2em] text-surface/55 mb-1">DIN</div>
                    <div className="font-mono text-surface">{d.din}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-[0.2em] text-surface/55 mb-1">Appointed</div>
                    <div className="font-medium text-surface">{d.appointedOn}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
